import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import IconButton from 'components/buttons/IconButton';
import { ScrollerContext } from 'components/Scroller/context';
import CloseIcon from 'components/svg/CloseIcon';
import { CAS_IMG_DEFAULT, SIGN_IN, URL_IMG_CAS } from 'contexts/CruiseSearchContext/constants';
import { getCookie } from 'lib/utilities/Cookies';

import sizes from 'styles/sizes';

// Components
import Scroller from 'components/Scroller';
import Styles from 'components/SideBarMenu/styles';

import { animateSideBarMenu, closeButtonAnimation, menuAnimation } from './animation';
import MenuItems from './MenuItems';

import { DEFAULT, SIDE_BAR_MENU } from 'components/Scroller/constants';
import { CruiseSearchContext } from 'contexts/CruiseSearchContext';
import { useFeatureFlags } from '../../hooks/LaunchDarkly/useFeatureFlags';
import MenuFooter from './MenuFooter';
import UserItems from './UserItems';

const { CloseButtonContainer, SideBar, ScrollingComponent, Overlay, Ul, Wrapper, MenuToolBar, ImgCAS, MenuLink, TierText } = Styles;

/**
 * Renders Side bar menu
 *
 * @param {string} state menu state, open/close
 * @param {func} onClick
 */

const SideBarMenu = ({ state, onClick }) => {
	const { universalNavigation } = useContext(CruiseSearchContext);
	const { showUserFavoritesFeature, showUserWatchlistFeature } = useFeatureFlags();

	const overlayRef = useRef();
	const timeline = useRef();
	const timeline2 = useRef();
	const timeline3 = useRef();
	const sideNavMenuRef = useRef();
	const menuRef = useRef();
	const closeButtonRef = useRef();
	const sideMenu = universalNavigation?.components.header.sideMenu;
	const favoritesAndWatchlist = universalNavigation?.components.header.favoritesAndWatchlist;

	let isMobileInitial = false;
	if (process.brower) {
		isMobileInitial = window?.innerWidth <= sizes.media.mediumMaxWidth;
	}

	const { [DEFAULT]: defaultScrollerContext } = useContext(ScrollerContext);
	const [showDropdown, setShowDropDown] = useState({ active: false, index: 0 });
	const [isMobile, setIsMobile] = useState(isMobileInitial);
	const [dataTier, setDataTier] = useState({});

	const { disableScroller: disableDefaultScroller, enableScroller: enableDefaultScroller } = useContext(defaultScrollerContext);

	const refs = useRef({
		sideNavMenuRef,
		overlayRef,
	});

	const refs2 = useRef({
		menuRef,
	});

	const refs3 = useRef({
		closeButtonRef,
	});

	const onResize = useCallback(() => {
		const isSmol = window?.innerWidth <= sizes.media.smallMaxWidth;
		if (isSmol !== isMobile) {
			setIsMobile(isSmol);
		}
	}, [setIsMobile, isMobile]);

	const onClose = () => {
		closeSideBarMenu();
	};

	const closeSideBarMenu = () => {
		const sideBarMenuAnimation = animateSideBarMenu({
			timeline,
			refs,
			onComplete: () => {},
			reversed: true,
			onReverseComplete: () => {
				onClick('close');
			},
		});
		closeButtonAnimation({
			timeline3,
			refs3,
			sideBarMenuAnimation,
			reversed: true,
			onReverseComplete: () => {},
			isMobile,
		});
	};

	const onSelectItem = (data) => {
		setShowDropDown(data);
	};

	const handleGoBack = () => {
		menuAnimation({ timeline2, refs2, onComplete: () => {}, reversed: true });
	};

	const Menu = useCallback(() => {
		if (state !== 'close') {
			return (
				<>
					<MenuToolBar>
						<MenuLink href={dataTier.path}>
							{dataTier.img && <ImgCAS src={dataTier.img} alt="CAS" />}
							<TierText>{dataTier.label}</TierText>
						</MenuLink>
					</MenuToolBar>
					<Ul ref={menuRef}>
						<UserItems
							goBack={handleGoBack}
							index={0}
							menu={favoritesAndWatchlist}
							onClick={onSelectItem}
							show={showDropdown}
							showFavorites={showUserFavoritesFeature}
							showWatchlist={showUserWatchlistFeature}
						/>

						{sideMenu.map((menu, index) => (
							<MenuItems key={index + 1} show={showDropdown} index={index + 1} onClick={onSelectItem} goBack={handleGoBack} menu={menu} />
						))}
						<MenuFooter />
					</Ul>
				</>
			);
		}

		return <></>;
	}, [state, showDropdown]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		onResize();

		if (state === 'open') {
			disableDefaultScroller();
			const mobileAnimation = closeButtonAnimation({
				timeline3,
				refs3,
				onComplete: () => {},
				reversed: false,
				onReverseComplete: () => {},
				isMobile,
			});
			animateSideBarMenu({ timeline, refs, mobileAnimation });
		}

		if (state === 'close') {
			enableDefaultScroller();
		}
	}, [state, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		menuAnimation({ timeline2, refs2, onComplete: () => {} });
	}, [showDropdown]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		window.addEventListener('resize', onResize);

		return () => {
			window.removeEventListener('resize', onResize);
		};
	}, [onResize, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

	const tierData = useCallback(() => {
		return getCookie('loyaltyData');
	}, [getCookie]);

	useEffect(() => {
		let newItem = {
			label: SIGN_IN,
			path: '/account/signin',
		};

		const data = Object.fromEntries(
			tierData()
				?.split('|')
				?.map((prop) => {
					const [propName, value] = prop.split('=') ?? null;
					return [propName, value === 'null' ? null : value];
				}) || [],
		);

		if (Object.keys(data).length > 0) {
			newItem = {
				label: data?.firstName,
				img: URL_IMG_CAS.replace('{{loyaltyLevel}}', data?.loyaltyLevel?.toLowerCase() || CAS_IMG_DEFAULT),
				path: '/account/signin',
			};
		}
		setDataTier(newItem);
	}, [tierData]);

	return (
		<Wrapper>
			<Overlay ref={overlayRef} onClick={onClose} />
			<SideBar ref={sideNavMenuRef}>
				<CloseButtonContainer>
					<IconButton passedRef={closeButtonRef} icon={<CloseIcon />} onClick={onClose} />
				</CloseButtonContainer>
				<Scroller id={SIDE_BAR_MENU} scrollingComponent={ScrollingComponent}>
					{<Menu />}
				</Scroller>
			</SideBar>
		</Wrapper>
	);
};

SideBarMenu.propTypes = {
	state: PropTypes.string,
	onClick: PropTypes.func,
};

export default SideBarMenu;
